import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

/**
 * The e-mail-OTP gate: address → 6-digit code typed in-app → session.
 *
 * A genuinely different flow from `TotpAuthGate`, not a variant of it. A magic
 * *link* opens in Safari, whose storage is separate from an installed PWA, so
 * the session never lands where the installed app can see it; a typed code
 * creates the session inside the app itself (ADR-0010 §2). Requires the
 * Supabase e-mail template to surface `{{ .Token }}` — RUNBOOK §18.
 *
 * Only `apps/staff` uses it today. It lives here anyway, alongside the TOTP
 * gate, because authentication is a platform concern (F8) — the point of the
 * package is that there is ONE place to fix an auth bug, and a second gate
 * sitting in an app would quietly recreate the problem.
 *
 * Client injected for the same reason as `TotpAuthGate`: a shared gate cannot
 * import any one app's `supabase` singleton.
 */

export interface OtpAuthClient {
  auth: {
    getSession(): Promise<{ data: { session: unknown | null } }>;
    signInWithOtp(o: {
      email: string;
      options?: { emailRedirectTo?: string };
    }): Promise<{ error: { message: string } | null }>;
    verifyOtp(o: { email: string; token: string; type: 'email' }): Promise<{ error: { message: string } | null }>;
    signOut(): Promise<unknown>;
    onAuthStateChange(cb: () => void): { data: { subscription: { unsubscribe(): void } } };
  };
}

export interface OtpAuthGateProps {
  client: OtpAuthClient;
  brand: string;
  /** Membership predicate, run once a session exists. Must not throw. */
  checkMembership: () => Promise<boolean>;
  deniedMessage: string;
  /** Where the e-mail link returns to. Defaults to the current origin. */
  redirectTo?: string;
  children: ReactNode;
}

type Stage = 'loading' | 'login' | 'codeSent' | 'denied' | 'ready';

export function OtpAuthGate({
  client,
  brand,
  checkMembership,
  deniedMessage,
  redirectTo,
  children,
}: OtpAuthGateProps) {
  const [stage, setStage] = useState<Stage>('loading');
  const [email, setEmail] = useState('');

  const evaluate = useCallback(async () => {
    const {
      data: { session },
    } = await client.auth.getSession();
    if (!session) {
      // Never bounce the user out of the code-entry screen: the OTP has been
      // sent and there is still no session by design.
      setStage((s) => (s === 'codeSent' ? 'codeSent' : 'login'));
      return;
    }
    setStage((await checkMembership()) ? 'ready' : 'denied');
  }, [client, checkMembership]);

  useEffect(() => {
    void evaluate();
    const { data: sub } = client.auth.onAuthStateChange(() => {
      void evaluate();
    });
    return () => sub.subscription.unsubscribe();
  }, [client, evaluate]);

  if (stage === 'loading') return <div className="boot">{brand} — chargement…</div>;
  if (stage === 'login')
    return (
      <Login
        client={client}
        brand={brand}
        redirectTo={redirectTo}
        onSent={(sentEmail) => {
          setEmail(sentEmail);
          setStage('codeSent');
        }}
      />
    );
  if (stage === 'codeSent')
    return (
      <EnterCode
        client={client}
        brand={brand}
        email={email}
        redirectTo={redirectTo}
        onBack={() => setStage('login')}
      />
    );
  if (stage === 'denied') return <NotMember client={client} brand={brand} message={deniedMessage} />;
  return <>{children}</>;
}

function Shell({ brand, title, children }: { brand: string; title: string; children: ReactNode }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">{brand}</div>
        <h1 className="auth-title">{title}</h1>
        {children}
      </div>
    </div>
  );
}

function Login({
  client,
  brand,
  redirectTo,
  onSent,
}: {
  client: OtpAuthClient;
  brand: string;
  redirectTo?: string;
  onSent: (email: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    const normalized = email.trim().toLowerCase();
    const { error } = await client.auth.signInWithOtp({
      email: normalized,
      options: { emailRedirectTo: redirectTo ?? window.location.origin },
    });
    setBusy(false);
    if (error) setErr(error.message);
    else onSent(normalized);
  };

  return (
    <Shell brand={brand} title="Connexion">
      <form onSubmit={submit} className="auth-form">
        <p className="auth-hint">
          Saisissez votre adresse e-mail : vous recevrez un code à 6 chiffres (aucun mot de passe).
        </p>
        <label className="auth-field">
          <span>Adresse e-mail</span>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        {err && <p className="auth-err">{err}</p>}
        <button className="auth-btn" type="submit" disabled={busy}>
          {busy ? '…' : 'Recevoir le code'}
        </button>
      </form>
    </Shell>
  );
}

function EnterCode({
  client,
  brand,
  email,
  redirectTo,
  onBack,
}: {
  client: OtpAuthClient;
  brand: string;
  email: string;
  redirectTo?: string;
  onBack: () => void;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [resent, setResent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    const { error } = await client.auth.verifyOtp({ email, token: code.trim(), type: 'email' });
    setBusy(false);
    // On success, onAuthStateChange re-evaluates and moves on — nothing to do.
    if (error) setErr(error.message);
  };

  const resend = async () => {
    setErr('');
    setResent(false);
    const { error } = await client.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo ?? window.location.origin },
    });
    if (error) setErr(error.message);
    else setResent(true);
  };

  return (
    <Shell brand={brand} title="Code de connexion">
      <form onSubmit={submit} className="auth-form">
        <p className="auth-hint">
          Un code à 6 chiffres a été envoyé à <strong>{email}</strong>. Saisissez-le ci-dessous.
        </p>
        <label className="auth-field">
          <span>Code</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            required
          />
        </label>
        {err && <p className="auth-err">{err}</p>}
        {resent && !err && <p className="auth-hint">Nouveau code envoyé.</p>}
        <button className="auth-btn" type="submit" disabled={busy || code.trim().length < 6}>
          {busy ? '…' : 'Valider'}
        </button>
      </form>
      <button type="button" className="auth-link" onClick={() => void resend()}>
        Renvoyer le code
      </button>
      <button type="button" className="auth-link" onClick={onBack}>
        Changer d’adresse
      </button>
    </Shell>
  );
}

function NotMember({ client, brand, message }: { client: OtpAuthClient; brand: string; message: string }) {
  return (
    <Shell brand={brand} title="Accès non autorisé">
      <p className="auth-hint">{message}</p>
      <button type="button" className="auth-btn" onClick={() => void client.auth.signOut()}>
        Se déconnecter
      </button>
    </Shell>
  );
}
